import { useState } from 'react'
import { ChevronDown, GripVertical } from 'lucide-react'
import { cn } from '@/lib/utils'

// Shared by the interactive-report and CFO-brief customizers: renders a flat
// `[{key,label,id,parent?}]` section registry as a checkbox tree. Nesting can
// run arbitrarily deep (e.g. CFO's "Full chart library" > "Overview charts" >
// "Participation status") – each level starts collapsed behind a "Customize
// (n)" toggle, and unchecking an ancestor greys out (but doesn't clear) every
// descendant, since only the ancestor's own hide actually removes them from
// the export.
function buildTree(sections) {
  const byKey = new Map(sections.map((s) => [s.key, { ...s, children: [] }]))
  const roots = []
  for (const node of byKey.values()) {
    const parent = node.parent && byKey.get(node.parent)
    if (parent) parent.children.push(node)
    else roots.push(node)
  }
  return roots
}

function SectionNode({ node, depth, isEnabled, onToggle, ancestorsEnabled, expanded, onToggleExpanded, drag }) {
  const enabled = isEnabled(node.key)
  const hasChildren = node.children.length > 0
  const isExpanded = expanded.has(node.key)
  const disabled = depth > 0 && !ancestorsEnabled
  // Only the major parts are draggable, and only when the caller wants ordering.
  const draggable = Boolean(drag) && depth === 0
  return (
    <li
      draggable={draggable || undefined}
      onDragStart={draggable ? () => drag.onDragStart(node.key) : undefined}
      onDragEnd={draggable ? drag.onDragEnd : undefined}
      onDragOver={draggable ? (event) => { event.preventDefault(); drag.onDragOver(node.key) } : undefined}
      onDrop={draggable ? (event) => { event.preventDefault(); drag.onDrop(node.key) } : undefined}
    >
      <div
        className={cn(
          'flex items-center gap-2 rounded border px-2 py-1.5 text-sm',
          depth === 0 ? 'border-border bg-background' : 'border-dashed border-border/60 bg-muted/20 text-xs',
          disabled && 'opacity-40',
          draggable && drag.dragKey === node.key && 'opacity-40',
          draggable && drag.overKey === node.key && drag.dragKey && drag.dragKey !== node.key && 'border-indigo-400 bg-indigo-50',
        )}
        style={depth > 0 ? { marginLeft: `${depth * 1.25}rem` } : undefined}
      >
        {draggable && <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-muted-foreground" aria-hidden="true" />}
        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={enabled}
            disabled={disabled}
            onChange={(event) => onToggle(node.key, event.target.checked)}
            className="h-3.5 w-3.5 shrink-0 accent-foreground"
          />
          <span className={cn('min-w-0 flex-1', depth === 0 && 'font-medium', enabled ? 'text-foreground' : 'text-muted-foreground line-through')}>{node.label}</span>
        </label>
        {hasChildren && (
          <button
            type="button"
            onClick={() => onToggleExpanded(node.key)}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            {isExpanded ? 'Hide items' : `Customize (${node.children.length})`}
            <ChevronDown className={cn('h-3 w-3 transition-transform', isExpanded && 'rotate-180')} />
          </button>
        )}
      </div>
      {hasChildren && isExpanded && (
        <ul className="mt-1 space-y-1">
          {node.children.map((child) => (
            <SectionNode
              key={child.key}
              node={child}
              depth={depth + 1}
              isEnabled={isEnabled}
              onToggle={onToggle}
              ancestorsEnabled={ancestorsEnabled && enabled}
              expanded={expanded}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

// `onReorder(fromKey, toKey)` opts the top level into drag-to-reorder; leave it
// out and the list is include/exclude only.
export default function SectionToggleList({ sections, isEnabled, onToggle, onReorder }) {
  const [expanded, setExpanded] = useState(() => new Set())
  const [dragKey, setDragKey] = useState(null)
  const [overKey, setOverKey] = useState(null)
  const toggleExpanded = (key) => setExpanded((prev) => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })
  const drag = onReorder ? {
    dragKey,
    overKey,
    onDragStart: setDragKey,
    onDragEnd: () => { setDragKey(null); setOverKey(null) },
    onDragOver: (key) => { if (key !== overKey) setOverKey(key) },
    onDrop: (key) => {
      if (dragKey && dragKey !== key) onReorder(dragKey, key)
      setDragKey(null); setOverKey(null)
    },
  } : null
  const tree = buildTree(sections)
  return (
    <ul className="space-y-1">
      {tree.map((node) => (
        <SectionNode
          key={node.key}
          node={node}
          depth={0}
          isEnabled={isEnabled}
          onToggle={onToggle}
          ancestorsEnabled
          expanded={expanded}
          onToggleExpanded={toggleExpanded}
          drag={drag}
        />
      ))}
    </ul>
  )
}
